/**
 * Modern Upload Interface Component
 * 
 * Redesigned upload interface with better UX:
 * - Compact, expandable design
 * - Modern visual styling
 * - Better mode selection
 * - Streamlined upload flow
 * - Improved visual feedback
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Upload,
  Globe,
  Type,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileUp,
  FolderOpen,
  RefreshCw,
  Plus,
  ChevronUp,
  ChevronDown,
  Sparkles,
  LayoutList,
  Layers,
  Info,
  KeyRound
} from 'lucide-react'

import { Button } from '../../ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card'
import { Select } from '../../ui/Select'
import { Input } from '../../ui/Input'
import { Switch } from '../../ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/Tooltip'
import { Textarea } from '../../ui/Textarea'
import { LinkDiscoveryModal } from './LinkDiscoveryModal'
import { useChatbots } from '../../../stores/chatbotStore'
import type { Chatbot } from '../../../types'
import { logger } from '../../../utils/logger'
import { cn } from '../../../utils/cn'
import { api } from '../../../services/api'

// Reuse existing types
type UploadMode = 'files' | 'url' | 'text'
type CrawlMode = 'individual' | 'verify'

interface UploadStatus {
  chatbot_id: string
  chatbot_name: string
  pending: number
  processing: number
  completed: number
  failed: number
  is_active: boolean
  total_files: number
  files: any[]
}

interface UploadProgress {
  isUploading: boolean
  progress: number
  currentFile: number
  totalFiles: number
  error?: string
  uploadingItems?: Array<{
    name: string
    status: 'pending' | 'processing' | 'completed' | 'failed'
  }>
}

interface ModernUploadInterfaceProps {
  selectedChatbot?: Chatbot | null
  onUploadComplete?: (result: any) => void
  onStatusUpdate?: (status: UploadStatus | null) => void
  className?: string
  refreshKey?: number
}

const SUPPORTED_FILE_TYPES = '.csv,.txt,.pdf,.json'
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB per file
const MAX_FILES_PER_UPLOAD = Number(import.meta.env.VITE_MAX_FILES_PER_UPLOAD) || 20
const MAX_TEXT_CHAR_LENGTH = 500_000

/**
 * Upload mode configuration with modern styling
 */
const uploadModes = {
  files: {
    icon: FileUp,
    title: 'Files',
    description: 'Upload CSV, PDF, TXT, JSON',
    color: 'blue',
    bgColor: 'bg-primary/10',
    iconColor: 'text-primary',
    borderColor: 'border-primary/20'
  },
  url: {
    icon: Globe,
    title: 'URLs',
    description: 'Process web pages & content',
    color: 'green',
    bgColor: 'bg-green-500/10',
    iconColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-500/20'
  },
  text: {
    icon: Type,
    title: 'Q&A',
    description: 'Questions & Answers',
    color: 'purple',
    bgColor: 'bg-violet-500/10',
    iconColor: 'text-violet-600 dark:text-violet-400',
    borderColor: 'border-violet-500/20'
  }
} as const

const crawlModes = {
  individual: 'Process this page only',
  verify: 'Discover and choose pages'
} as const

/**
 * ModernUploadInterface Component
 */
export const ModernUploadInterface: React.FC<ModernUploadInterfaceProps> = ({
  selectedChatbot,
  onUploadComplete,
  onStatusUpdate,
  className,
  refreshKey
}) => {
  // ====================================
  // STATE MANAGEMENT
  // ====================================
  
  const [isExpanded, setIsExpanded] = useState(false)
  const [uploadMode, setUploadMode] = useState<UploadMode>('files')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [folderMode, setFolderMode] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
    currentFile: 0,
    totalFiles: 0
  })
  const [suggestMetadata, setSuggestMetadata] = useState(true)
  const [chunkingStrategy, setChunkingStrategy] = useState<'auto' | 'individual' | 'semantic_groups'>('auto')

  // URL upload state
  const [urlInput, setUrlInput] = useState('')
  const [crawlMode, setCrawlMode] = useState<CrawlMode>('individual')
  const [urlQueue, setUrlQueue] = useState<string[]>([])
  
  // Q&A upload state
  const [qaPairs, setQaPairs] = useState<Array<{ question: string; answer: string }>>([
    { question: '', answer: '' }
  ])
  const [textTitle, setTextTitle] = useState('')
  
  // Required variables state (for multi-user isolation / auto_train)
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})

  // Status monitoring state
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null)
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [showFileDetails, setShowFileDetails] = useState(false)
  
  // Link discovery state
  const [isLinkDiscoveryOpen, setIsLinkDiscoveryOpen] = useState(false)
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const monitoringIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    return () => {
      if (monitoringIntervalRef.current) {
        clearInterval(monitoringIntervalRef.current)
      }
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Check for active uploads when component mounts or chatbot changes
   */
  useEffect(() => {
    if (selectedChatbot) {
      // Check for any active background processing when component loads
      // Add a small delay to ensure the page has fully loaded
      const timeoutId = setTimeout(() => {
        checkUploadStatus()
      }, 500)
      
      return () => clearTimeout(timeoutId)
    } else {
      // Clean up when no chatbot is selected
      stopStatusMonitoring()
      setUploadProgress({
        isUploading: false,
        progress: 0,
        currentFile: 0,
        totalFiles: 0
      })
    }
  }, [selectedChatbot])

  // Re-check status when parent signals a change (e.g., cancel/retry)
  useEffect(() => {
    if (refreshKey && selectedChatbot) {
      checkUploadStatus()
    }
  }, [refreshKey])

  /**
   * Clean up monitoring when chatbot changes
   */
  useEffect(() => {
    return () => {
      // Stop any ongoing monitoring when chatbot changes
      stopStatusMonitoring()
    }
  }, [selectedChatbot?.id])

  useEffect(() => {
    if (uploadMode !== 'files') {
      setValidationError(null)
    }
  }, [uploadMode])

  // ====================================
  // REQUIRED VARIABLES (multi-user isolation)
  // ====================================

  /**
   * Get variables that are both required and auto_train from the chatbot config.
   * These must be sent as custom_metadata during upload.
   */
  const getRequiredAutoTrainVars = useCallback((): Array<{ name: string; default?: string }> => {
    if (!selectedChatbot?.settings) return []
    const varConfig = (selectedChatbot.settings as any)?.variables || {}
    const result: Array<{ name: string; default?: string }> = []
    for (const [name, settings] of Object.entries(varConfig)) {
      if (typeof settings === 'object' && settings !== null) {
        const s = settings as any
        if (s.required && s.auto_train) {
          result.push({ name, default: s.default || '' })
        }
      }
    }
    return result
  }, [selectedChatbot])

  const requiredVars = getRequiredAutoTrainVars()

  // Build custom_metadata from filled-in variable values
  const buildCustomMetadata = useCallback((): Record<string, string> | null => {
    if (requiredVars.length === 0) return null
    const metadata: Record<string, string> = {}
    for (const v of requiredVars) {
      const val = variableValues[v.name]?.trim()
      if (val) metadata[v.name] = val
    }
    return Object.keys(metadata).length > 0 ? metadata : null
  }, [requiredVars, variableValues])

  // Check if all required variables are filled
  const allRequiredVarsFilled = requiredVars.length === 0 || requiredVars.every(v => variableValues[v.name]?.trim())

  // Reset variable values when chatbot changes
  useEffect(() => {
    setVariableValues({})
  }, [selectedChatbot?.id])

  // ====================================
  // FILE HANDLING
  // ====================================

  const processSelectedFiles = useCallback(async (files: File[]) => {
    // Enforce max files per upload
    if (files.length > MAX_FILES_PER_UPLOAD) {
      setValidationError(`Too many files. Maximum is ${MAX_FILES_PER_UPLOAD} per upload.`)
      return
    }

    const withinSizeLimit = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        logger.warn('File too large', {
          component: 'ModernUploadInterface',
          action: 'file_size_validation',
          fileName: file.name,
          size: file.size,
          maxSize: MAX_FILE_SIZE
        })
        return false
      }
      return true
    })

    const charLimitErrors: string[] = []
    const validatedFiles: File[] = []

    for (const file of withinSizeLimit) {
      const extension = file.name.split('.').pop()?.toLowerCase() || ''
      const requiresCharValidation = ['txt', 'json'].includes(extension)

      if (requiresCharValidation) {
        try {
          const textContent = await file.text()
          if (textContent.length > MAX_TEXT_CHAR_LENGTH) {
            charLimitErrors.push(`${file.name} exceeds the ${MAX_TEXT_CHAR_LENGTH.toLocaleString()} character limit`)
            continue
          }
        } catch (error) {
          logger.error('Failed to read file for validation', {
            component: 'ModernUploadInterface',
            action: 'file_read_error',
            fileName: file.name,
            error
          })
          charLimitErrors.push(`Could not read ${file.name}. Please try again.`)
          continue
        }
      }

      validatedFiles.push(file)
    }

    setSelectedFiles(validatedFiles)
    setValidationError(charLimitErrors.length ? charLimitErrors.join(' • ') : null)

    logger.debug('File selection processed', {
      component: 'ModernUploadInterface',
      action: 'file_validation_complete',
      validFiles: validatedFiles.length,
      totalFiles: files.length,
      rejectedFiles: files.length - validatedFiles.length
    })
  }, [])

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    void processSelectedFiles(files)
  }, [processSelectedFiles])

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.webkitdirectory = folderMode
      fileInputRef.current.click()
    }
  }

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    const files = Array.from(event.dataTransfer.files)
    void processSelectedFiles(files)
  }, [processSelectedFiles])

  // ====================================
  // URL VALIDATION
  // ====================================

  /**
   * Comprehensive URL validation
   */
  const validateUrl = (url: string): { isValid: boolean; error?: string } => {
    const trimmedUrl = url.trim()
    
    // Check if empty
    if (!trimmedUrl) {
      return { isValid: false, error: 'URL cannot be empty' }
    }
    
    // Check basic URL format
    try {
      const urlObj = new URL(trimmedUrl)
      
      // Check protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' }
      }
      
      // Check if hostname exists
      if (!urlObj.hostname || urlObj.hostname.length === 0) {
        return { isValid: false, error: 'URL must have a valid domain' }
      }
      
      // Check for localhost/local IPs (security concern)
      if (urlObj.hostname === 'localhost' || 
          urlObj.hostname.startsWith('127.') || 
          urlObj.hostname.startsWith('192.168.') ||
          urlObj.hostname.startsWith('10.') ||
          urlObj.hostname.startsWith('172.')) {
        return { isValid: false, error: 'Local URLs are not allowed for security reasons' }
      }
      
      // Check for valid domain format
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/
      if (!domainRegex.test(urlObj.hostname)) {
        return { isValid: false, error: 'URL domain format is invalid' }
      }
      
      // Check URL length (reasonable limit)
      if (trimmedUrl.length > 2048) {
        return { isValid: false, error: 'URL is too long (maximum 2048 characters)' }
      }
      
      return { isValid: true }
      
    } catch (error) {
      return { isValid: false, error: 'Invalid URL format' }
    }
  }

  /**
   * Validate multiple URLs
   */
  const validateUrls = (urls: string[]): { isValid: boolean; error?: string; invalidUrls?: string[] } => {
    if (urls.length === 0) {
      return { isValid: false, error: 'No URLs provided' }
    }
    
    const invalidUrls: string[] = []
    
    for (const url of urls) {
      const validation = validateUrl(url)
      if (!validation.isValid) {
        invalidUrls.push(url)
      }
    }
    
    if (invalidUrls.length > 0) {
      return { 
        isValid: false, 
        error: `${invalidUrls.length} invalid URL(s) found`,
        invalidUrls 
      }
    }
    
    return { isValid: true }
  }

  // ====================================
  // UPLOAD OPERATIONS (Simplified versions)
  // ====================================

  const uploadFiles = async () => {
    if (!selectedChatbot || selectedFiles.length === 0) return

    try {
      setUploadProgress({
        isUploading: true,
        progress: 0,
        currentFile: 0,
        totalFiles: selectedFiles.length
      })

      const formData = new FormData()
      selectedFiles.forEach(file => formData.append('files', file))
      formData.append('chatbot_id', selectedChatbot.id)
      formData.append('suggest_metadata', suggestMetadata ? 'true' : 'false')
      formData.append('chunking_strategy', chunkingStrategy)
      const customMeta = buildCustomMetadata()
      if (customMeta) {
        formData.append('custom_metadata', JSON.stringify(customMeta))
      }

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      logger.debug('File upload completed', {
        component: 'ModernUploadInterface',
        action: 'upload_complete',
        data: { result: response.data }
      })

      // Start monitoring background processing
      startStatusMonitoring()
      
      // Immediately check status to show Processing Status section
      setTimeout(() => checkUploadStatus(), 1000)
      
      // Set a fallback timeout to clear processing state if monitoring fails
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }
      uploadTimeoutRef.current = setTimeout(() => {
        logger.warn('File upload timeout reached, clearing processing state', {
          component: 'ModernUploadInterface',
          action: 'file_upload_timeout_fallback'
        })
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0
        })
        setIsExpanded(false)
      }, 5 * 60 * 1000) // 5 minute timeout

      // Clear upload progress — Processing Status section handles the rest
      setUploadProgress({
        isUploading: false,
        progress: 0,
        currentFile: 0,
        totalFiles: 0
      })

      setSelectedFiles([])
      setValidationError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      
      // Don't collapse immediately - wait for processing to complete
      // setIsExpanded(false)

    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || (error instanceof Error ? error.message : 'Upload failed')
      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        error: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)
      }))
    }
  }

  /**
   * Add URL to queue
   */
  const addUrlToQueue = () => {
    const url = urlInput.trim()
    if (!url) return
    
    // Comprehensive URL validation
    const validation = validateUrl(url)
    if (!validation.isValid) {
      setUploadProgress(prev => ({
        ...prev,
        error: validation.error || 'Invalid URL'
      }))
      return
    }
    
    // Check if URL already in queue
    if (urlQueue.includes(url)) {
      setUploadProgress(prev => ({
        ...prev,
        error: 'URL already added to queue'
      }))
      return
    }
    
    setUrlQueue(prev => [...prev, url])
    setUrlInput('')
    
    // Clear any previous errors
    setUploadProgress(prev => ({
      ...prev,
      error: undefined
    }))
  }

  /**
   * Remove URL from queue
   */
  const removeUrlFromQueue = (index: number) => {
    setUrlQueue(prev => prev.filter((_, i) => i !== index))
  }

  /**
   * Clear entire URL queue
   */
  const clearUrlQueue = () => {
    setUrlQueue([])
  }

  const processUrl = async (urlsToProcess?: string[], retryCount = 0) => {
    // Use queue URLs if available, otherwise single URL input
    const urlsToUse = urlsToProcess || (urlQueue.length > 0 ? urlQueue : [urlInput.trim()])
    
    if (!selectedChatbot || urlsToUse.length === 0) return

    // Validate all URLs before processing
    const validation = validateUrls(urlsToUse)
    if (!validation.isValid) {
      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        error: validation.error || 'Invalid URLs detected'
      }))
      return
    }

    // For verify mode with single URL, check if we should open discovery modal first
    if (crawlMode === 'verify' && !urlsToProcess && urlsToUse.length === 1) {
      setIsLinkDiscoveryOpen(true)
      return
    }

    try {
      setUploadProgress({
        isUploading: true,
        progress: 0,
        currentFile: 1,
        totalFiles: urlsToUse.length
      })

      let response

      // Use different endpoint for selected URLs from link discovery
      if (urlsToProcess && urlsToProcess.length > 1) {
        // Selected URLs from link discovery - use chatbot-level status monitoring like file uploads
        logger.debug('Processing selected URLs from link discovery', {
          component: 'ModernUploadInterface',
          action: 'process_selected_urls',
          data: { count: urlsToProcess.length }
        })
        
        await api.post('/upload-selected-urls', {
          main_url: urlInput.trim() || urlsToProcess[0],
          selected_urls: urlsToProcess,
          chatbot_id: selectedChatbot.id,
          suggest_metadata: suggestMetadata,
          ...(buildCustomMetadata() && { custom_metadata: buildCustomMetadata() })
        })
        
        // Use the same reliable chatbot-level monitoring as file uploads
        startStatusMonitoring()
        
        // Immediately check status to show Processing Status section
        setTimeout(() => checkUploadStatus(), 1000)
        
        // Set a fallback timeout to clear processing state if monitoring fails
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current)
        }
        uploadTimeoutRef.current = setTimeout(() => {
          logger.warn('URL upload timeout reached, clearing processing state', {
            component: 'ModernUploadInterface',
            action: 'url_upload_timeout_fallback'
          })
          setUploadProgress({
            isUploading: false,
            progress: 0,
            currentFile: 0,
            totalFiles: 0
          })
          setIsExpanded(false)
        }, 5 * 60 * 1000) // 5 minute timeout
        
        // Clear upload progress — Processing Status section handles the rest
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0
        })
        
      } else {
        // Regular URL processing (single URL or queued URLs) - now with proper monitoring
        logger.debug('Processing regular URLs', {
          component: 'ModernUploadInterface',
          action: 'process_regular_urls',
          data: {
            count: urlsToUse.length,
            mode: crawlMode
          }
        })
        
        // Set uploading state BEFORE the API call to ensure UI shows processing
        setUploadProgress({
          isUploading: true,  // Set this first to ensure processing status shows
          progress: 0,
          currentFile: 0,
          totalFiles: urlsToUse.length
        })
        
        await api.post('/upload-urls', {
          urls: urlsToUse,
          chatbot_id: selectedChatbot.id,
          mode: crawlMode,
          suggest_metadata: suggestMetadata,
          ...(buildCustomMetadata() && { custom_metadata: buildCustomMetadata() })
        })
        
        // Start the same reliable chatbot-level monitoring as file uploads
        startStatusMonitoring()
        
        // Immediately check status to show Processing Status section
        setTimeout(() => checkUploadStatus(), 1000)
        
        // Set a fallback timeout to clear "Processing..." state if monitoring fails
        if (uploadTimeoutRef.current) {
          clearTimeout(uploadTimeoutRef.current)
        }
        uploadTimeoutRef.current = setTimeout(() => {
          logger.warn('URL upload timeout reached, clearing processing state', {
            component: 'ModernUploadInterface',
            action: 'url_upload_timeout_fallback'
          })
          setUploadProgress({
            isUploading: false,
            progress: 0,
            currentFile: 0,
            totalFiles: 0
          })
          setIsExpanded(false)
        }, 5 * 60 * 1000) // 5 minute timeout
        
        // Clear upload progress — Processing Status section handles the rest
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0
        })
        
        logger.debug('URL processing initiated, starting status monitoring', {
          component: 'ModernUploadInterface',
          action: 'url_processing_with_monitoring',
          data: { urlCount: urlsToUse.length }
        })
      }
      
      // Clear inputs after successful submission
      setUrlInput('')
      setUrlQueue([])
      
      // Don't clear progress - let monitoring handle the rest

    } catch (error: any) {
      let errorMessage = error?.response?.data?.detail
        || (error instanceof Error ? error.message : 'URL processing failed')
      if (typeof errorMessage !== 'string') errorMessage = JSON.stringify(errorMessage)

      // Handle 409 conflict with retry
      if (typeof errorMessage === 'string' && errorMessage.includes('409') && retryCount < 2) {
        logger.debug('409 conflict detected, retrying after delay', {
          component: 'ModernUploadInterface',
          action: 'retry_after_conflict',
          data: { retryCount: retryCount + 1 }
        })
        
        // Wait a bit and retry
        setTimeout(() => {
          processUrl(urlsToProcess, retryCount + 1)
        }, 1000 * (retryCount + 1)) // Progressive delay: 1s, 2s
        return
      } else if (typeof errorMessage === 'string' && errorMessage.includes('409')) {
        errorMessage = 'Another upload is still in progress. Please wait a moment and try again manually.'
      }
      
      logger.error('URL processing failed', {
        component: 'ModernUploadInterface',
        action: 'url_process_error',
        error: errorMessage,
        data: { retryCount }
      })
      
      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        error: errorMessage
      }))
    }
  }

  const processText = async () => {
    if (!selectedChatbot) return

    // Validate Q&A pairs - at least one pair must have both question and answer
    const validPairs = qaPairs.filter(pair => pair.question.trim() && pair.answer.trim())
    if (validPairs.length === 0) {
      setUploadProgress({
        isUploading: false,
        progress: 0,
        currentFile: 0,
        totalFiles: 0,
        error: 'Please enter at least one complete Q&A pair'
      })
      return
    }

    for (const pair of validPairs) {
      const combinedContent = `Question: ${pair.question.trim()}\n\nAnswer: ${pair.answer.trim()}`
      if (combinedContent.length > MAX_TEXT_CHAR_LENGTH) {
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0,
          error: `Each Q&A pair must stay under ${MAX_TEXT_CHAR_LENGTH.toLocaleString()} characters`
        })
        return
      }
    }

    try {
      // Initialize upload progress with individual Q&A pair tracking
      const uploadingPairs = validPairs.map((pair, i) => ({
        id: i,
        question: pair.question.trim(),
        answer: pair.answer.trim(),
        status: 'pending' as 'pending' | 'processing' | 'completed' | 'failed'
      }))

      setUploadProgress({
        isUploading: true,
        progress: 0,
        currentFile: 0,
        totalFiles: validPairs.length,
        uploadingItems: uploadingPairs.map(p => ({
          name: `Q&A Pair ${p.id + 1}`,
          status: 'pending'
        }))
      })

      // Mark all as processing
      setUploadProgress(prev => ({
        ...prev,
        progress: 10,
        uploadingItems: uploadingPairs.map(p => ({
          name: `Q&A Pair ${p.id + 1}`,
          status: 'processing'
        }))
      }))

      // Send all Q&A pairs in a single request
      const response = await api.post('/upload-text', {
        qa_pairs: validPairs.map(pair => ({
          question: pair.question.trim(),
          answer: pair.answer.trim()
        })),
        title: textTitle.trim() || 'Q&A Collection',
        chatbot_id: selectedChatbot.id,
        suggest_metadata: suggestMetadata,
        ...(buildCustomMetadata() && { custom_metadata: buildCustomMetadata() })
      })

      // Clear upload progress — Processing Status section handles the rest
      setUploadProgress({
        isUploading: false,
        progress: 0,
        currentFile: 0,
        totalFiles: 0
      })

      logger.debug('Q&A upload accepted', {
        component: 'ModernUploadInterface',
        action: 'qa_upload_queued',
        data: {
          pairs_count: validPairs.length,
          status: response.data?.status || 'unknown',
          session_id: response.data?.session_id
        }
      })

      // Start monitoring background processing like files and URLs
      startStatusMonitoring()

      // Immediately check status to show Processing Status section
      setTimeout(() => checkUploadStatus(), 1000)
      
      // Set a fallback timeout to clear processing state if monitoring fails
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }
      uploadTimeoutRef.current = setTimeout(() => {
        logger.warn('Text upload timeout reached, clearing processing state', {
          component: 'ModernUploadInterface',
          action: 'text_upload_timeout_fallback'
        })
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0
        })
        setIsExpanded(false)
      }, 5 * 60 * 1000) // 5 minute timeout

      // Clear Q&A inputs after successful submission
      setQaPairs([{ question: '', answer: '' }])
      setTextTitle('')

      // Don't auto-clear progress or collapse - let monitoring handle completion

    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || (error instanceof Error ? error.message : 'Text processing failed')
      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        error: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)
      }))
    }
  }

  const handleUpload = () => {
    switch (uploadMode) {
      case 'files': return uploadFiles()
      case 'url': return processUrl()
      case 'text': return processText()
    }
  }

  // ====================================
  // STATUS MONITORING
  // ====================================

  /**
   * Start status monitoring with polling
   */
  const startStatusMonitoring = () => {
    if (!selectedChatbot || isMonitoring) return

    logger.debug('Starting status monitoring', {
      component: 'ModernUploadInterface',
      action: 'monitoring_start',
      data: { chatbotId: selectedChatbot.id }
    })

    setIsMonitoring(true)
    
    // Clear any existing interval
    if (monitoringIntervalRef.current) {
      clearInterval(monitoringIntervalRef.current)
    }

    // Start polling every 2 seconds
    monitoringIntervalRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/upload-status/${selectedChatbot.id}`)
        const status = response.data
        setUploadStatus(status)
        onStatusUpdate?.(status)

        logger.info('📊 Status monitoring update', {
          component: 'ModernUploadInterface',
          action: 'monitoring_update',
          data: {
            status: {
              pending: status.pending,
              processing: status.processing,
              completed: status.completed,
              failed: status.failed,
              is_active: status.is_active,
              total_files: status.total_files
            }
          }
        })

        // Stop monitoring if processing is TRULY complete (stronger condition)
        const isTrulyComplete = (
          !status.is_active && 
          status.pending === 0 && 
          status.processing === 0 &&
          (status.completed > 0 || status.failed > 0) // Ensure we actually processed something
        )
        
        // Debug logging for completion check
        logger.info('🔍 Completion check evaluation', {
          component: 'ModernUploadInterface',
          action: 'completion_check',
          data: {
            is_active: status.is_active,
            pending: status.pending,
            processing: status.processing,
            completed: status.completed,
            failed: status.failed,
            isTrulyComplete,
            conditions: {
              not_active: !status.is_active,
              no_pending: status.pending === 0,
              no_processing: status.processing === 0,
              has_results: (status.completed > 0 || status.failed > 0)
            }
          }
        })
        
        if (isTrulyComplete) {
          logger.info('🎉 COMPLETION TRIGGERED - Processing completed, stopping monitoring', {
            component: 'ModernUploadInterface',
            action: 'monitoring_complete',
            data: {
              finalStatus: status,
              condition: 'truly_complete'
            }
          })

          // Check for warnings in file_details
          if (status.file_details && Array.isArray(status.file_details)) {
            const filesWithWarnings = status.file_details.filter((file: any) => file.warning)
            if (filesWithWarnings.length > 0) {
              const warningMessages = filesWithWarnings.map((file: any) =>
                `${file.filename}: ${file.warning}`
              ).join('\n')
              alert(`⚠️ Some files were truncated:\n\n${warningMessages}`)
            }
          }

          // Clear upload progress state to remove "Processing..." display
          setUploadProgress({
            isUploading: false,
            progress: 0,
            currentFile: 0,
            totalFiles: 0
          })

          stopStatusMonitoring()

          // Clear any pending timeout since we completed successfully
          if (uploadTimeoutRef.current) {
            clearTimeout(uploadTimeoutRef.current)
            uploadTimeoutRef.current = null
          }

          // Trigger data refresh and collapse
          if (onUploadComplete) {
            onUploadComplete(status)
          }

          // Show completion status for a few seconds before clearing
          setTimeout(() => {
            setUploadStatus(null)
            setIsExpanded(false) // Collapse after processing completes
            onStatusUpdate?.(null)
          }, 3000) // Show completed status for 3 seconds
        } else if (status.pending > 0 || status.processing > 0) {
          // Log when we're still processing (for debugging)
          logger.info('⏳ Still processing, keeping monitoring active', {
            component: 'ModernUploadInterface',
            action: 'monitoring_continue',
            data: {
              pending: status.pending,
              processing: status.processing,
              completed: status.completed,
              is_active: status.is_active
            }
          })
        }
      } catch (error) {
        logger.warn('Status monitoring error', {
          component: 'ModernUploadInterface',
          action: 'monitoring_error',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        
        // If we get repeated errors, stop monitoring
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        if (errorMessage.includes('404') || errorMessage.includes('403')) {
          stopStatusMonitoring()
        }
      }
    }, 2000)
  }

  /**
   * Stop status monitoring
   */
  const stopStatusMonitoring = () => {
    logger.debug('Stopping status monitoring', {
      component: 'ModernUploadInterface',
      action: 'monitoring_stop'
    })

    setIsMonitoring(false)
    
    if (monitoringIntervalRef.current) {
      clearInterval(monitoringIntervalRef.current)
      monitoringIntervalRef.current = null
    }
    
    // Only clear if we're not in the middle of an upload
    if (!uploadStatus || 
        (uploadStatus.pending === 0 && uploadStatus.processing === 0) || 
        !uploadStatus.is_active) {
      setUploadStatus(null)
      onStatusUpdate?.(null)
      setUploadProgress({
        isUploading: false,
        progress: 0,
        currentFile: 0,
        totalFiles: 0
      })
      logger.debug('Cleared upload progress state', {
        component: 'ModernUploadInterface',
        action: 'clear_upload_progress'
      })
    } else {
      logger.debug('Keeping upload progress state active despite monitoring stop', {
        component: 'ModernUploadInterface',
        action: 'keep_upload_progress',
        data: { uploadStatus }
      })
    }
  }


  /**
   * Check current upload status manually
   */
  const checkUploadStatus = async () => {
    if (!selectedChatbot) return

    try {
      logger.info('🔍 Checking for ongoing background processing', {
        component: 'ModernUploadInterface',
        action: 'check_status_on_load',
        data: { chatbotId: selectedChatbot.id }
      })

      const response = await api.get(`/upload-status/${selectedChatbot.id}`)
      const status = response.data
      setUploadStatus(status)
      onStatusUpdate?.(status)

      logger.info('📊 Upload status received on page load', {
        component: 'ModernUploadInterface',
        action: 'status_received_on_load',
        data: {
          status: {
            is_active: status.is_active,
            pending: status.pending,
            processing: status.processing,
            completed: status.completed,
            failed: status.failed,
            total_files: status.total_files
          }
        }
      })

      // Start monitoring if there's actual work to be done
      if (status.pending > 0 || status.processing > 0) {
        logger.info('🔄 RESTORING: Active processing detected, restoring UI state', {
          component: 'ModernUploadInterface',
          action: 'restore_monitoring_state',
          pending: status.pending,
          processing: status.processing,
          totalFiles: status.total_files,
          willExpand: true,
          willStartMonitoring: !isMonitoring
        })
        
        // Expand the interface to show Processing Status section
        setIsExpanded(true)
        
        // Start monitoring if not already active
        if (!isMonitoring) {
          logger.info('▶️ Starting status monitoring for ongoing processing', {
            component: 'ModernUploadInterface',
            action: 'start_monitoring_on_restore'
          })
          startStatusMonitoring()
        }
      } else {
        // If there's nothing happening, clear both status and any lingering upload progress
        setUploadStatus(null)
        onStatusUpdate?.(null)
        setUploadProgress({
          isUploading: false,
          progress: 0,
          currentFile: 0,
          totalFiles: 0
        })
      }
      
    } catch (error) {
      logger.error('Manual status check failed', {
        component: 'ModernUploadInterface',
        action: 'manual_status_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  const canUpload = () => {
    if (!selectedChatbot || uploadProgress.isUploading || !allRequiredVarsFilled) return false

    switch (uploadMode) {
      case 'files': return selectedFiles.length > 0 && !validationError
      case 'url': {
        const hasInput = urlInput.trim().length > 0 || urlQueue.length > 0
        if (!hasInput) return false
        
        // Check validation for current input if present
        if (urlInput.trim()) {
          const validation = validateUrl(urlInput.trim())
          return validation.isValid
        }
        
        // If no current input but has queue, check if queue is valid
        return urlQueue.length > 0
      }
      case 'text': {
        // At least one Q&A pair must have both question and answer
        return qaPairs.some(pair => pair.question.trim() && pair.answer.trim())
      }
      default: return false
    }
  }

  // ====================================
  // RENDER HELPERS
  // ====================================

  /**
   * Compact header with expand/collapse
   */
  const renderCompactHeader = () => {
    if (!selectedChatbot) {
      return (
        <div className={cn("border border-dashed border-border rounded-xl bg-muted/50 p-8 text-center", className)}>
          <AlertCircle className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Select a chatbot to upload training data</p>
        </div>
      )
    }

    return (
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl border bg-card transition-all hover:shadow-sm",
          isExpanded ? "border-primary/20 shadow-sm" : "border-border hover:border-border",
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Upload className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground">Add Training Data</p>
            <p className="text-xs text-muted-foreground">
              Upload to <span className="font-medium text-foreground">{selectedChatbot.name}</span>
            </p>
          </div>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-muted-foreground transition-transform",
          isExpanded && "rotate-180"
        )} />
      </button>
    )
  }

  /**
   * Mode selector with modern styling
   */
  const renderModeSelector = () => (
    <div className="grid grid-cols-3 gap-2.5 mb-5">
      {Object.entries(uploadModes).map(([mode, config]) => {
        const Icon = config.icon
        const isActive = uploadMode === mode

        return (
          <button
            key={mode}
            onClick={() => setUploadMode(mode as UploadMode)}
            disabled={uploadProgress.isUploading}
            className={cn(
              "flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-all",
              isActive
                ? cn(config.bgColor, config.borderColor)
                : "bg-card border-border hover:border-border hover:bg-muted/50"
            )}
          >
            <div className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              isActive ? config.bgColor : "bg-muted"
            )}>
              <Icon className={cn("w-4 h-4", isActive ? config.iconColor : "text-muted-foreground")} />
            </div>
            <div className="min-w-0">
              <p className={cn("text-sm font-medium leading-tight", isActive ? "text-foreground" : "text-muted-foreground")}>
                {config.title}
              </p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5 hidden sm:block">{config.description}</p>
            </div>
          </button>
        )
      })}
    </div>
  )

  /**
   * Compact file upload interface
   */
  const renderFileUpload = () => (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={cn(
          "group relative border border-dashed rounded-xl p-8 text-center transition-all cursor-pointer",
          uploadProgress.isUploading
            ? "border-border bg-muted/50 cursor-not-allowed"
            : "border-border bg-card hover:border-primary/40 hover:bg-primary/5"
        )}
        onClick={uploadProgress.isUploading ? undefined : handleFileInputClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORTED_FILE_TYPES}
          multiple={!folderMode}
          onChange={handleFileSelect}
          className="hidden"
        />
        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            uploadProgress.isUploading ? "bg-muted" : "bg-primary/10 group-hover:bg-primary/15"
          )}>
            {folderMode ? (
              <FolderOpen className="w-5 h-5 text-primary" />
            ) : (
              <Upload className="w-5 h-5 text-primary" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {folderMode ? 'Select a folder' : (
                <>
                  <span className="text-primary">Click to upload</span> or drag and drop
                </>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              CSV, PDF, TXT, JSON — max 50 MB per file, up to 20 files
            </p>
          </div>
        </div>
      </div>

      {/* Selected files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground">
            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
          </span>
          <div className="max-h-24 overflow-y-auto space-y-1.5">
            {selectedFiles.slice(0, 3).map((file, index) => (
              <div key={index} className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm">
                <FileUp className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate flex-1 text-foreground">{file.name}</span>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {file.size < 1000 * 1024
                    ? `${Math.round(file.size / 1024)} KB`
                    : `${(file.size / 1024 / 1024).toFixed(1)} MB`
                  }
                </span>
              </div>
            ))}
            {selectedFiles.length > 3 && (
              <p className="text-xs text-muted-foreground text-center py-1">
                +{selectedFiles.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}

      {validationError && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {validationError}
        </div>
      )}
    </div>
  )

  /**
   * Enhanced URL upload interface with queue
   */
  const renderUrlUpload = () => {
    // Real-time URL validation for input field
    const currentUrlValidation = urlInput.trim() ? validateUrl(urlInput.trim()) : null
    const hasValidationError = Boolean(currentUrlValidation && !currentUrlValidation.isValid)
    
    return (
      <div className="space-y-4">
        {/* URL input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/page"
              disabled={uploadProgress.isUploading}
              className={cn(
                hasValidationError && "border-red-300 focus:border-red-500 focus:ring-red-500"
              )}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addUrlToQueue()
                }
              }}
            />
            {hasValidationError && currentUrlValidation && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {currentUrlValidation.error}
              </p>
            )}
          </div>
          <Button
            type="button"
            onClick={addUrlToQueue}
            disabled={!urlInput.trim() || uploadProgress.isUploading || hasValidationError}
            variant="outline"
            size="sm"
            className="px-3 h-10 self-start"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Crawl mode toggle */}
        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
          {Object.entries(crawlModes).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setCrawlMode(mode as CrawlMode)}
              disabled={uploadProgress.isUploading}
              className={cn(
                "flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all text-center",
                crawlMode === mode
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* URL queue */}
        {urlQueue.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">{urlQueue.length} URL{urlQueue.length > 1 ? 's' : ''} queued</span>
              <button
                onClick={clearUrlQueue}
                disabled={uploadProgress.isUploading}
                className="text-xs text-red-500 hover:text-red-700 transition-colors"
              >
                Clear all
              </button>
            </div>
            <div className="max-h-28 overflow-y-auto space-y-1.5">
              {urlQueue.map((url, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm group">
                  <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="truncate flex-1 text-foreground">{url}</span>
                  <button
                    onClick={() => removeUrlFromQueue(index)}
                    disabled={uploadProgress.isUploading}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Link discovery hint */}
        {crawlMode !== 'verify' && (urlInput.trim() || urlQueue.length > 0) && (
          <button
            type="button"
            onClick={() => setCrawlMode('verify')}
            disabled={uploadProgress.isUploading}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-primary/10 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
          >
            <Globe className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-primary">Want to choose specific pages?</p>
              <p className="text-xs text-primary mt-0.5">Switch to discovery mode to find and select links</p>
            </div>
          </button>
        )}
      </div>
    )
  }

  /**
   * Q&A upload interface with multiple question-answer pairs
   */
  const renderTextUpload = () => {
    const addQAPair = () => {
      setQaPairs([...qaPairs, { question: '', answer: '' }])
    }

    const removeQAPair = (index: number) => {
      if (qaPairs.length > 1) {
        setQaPairs(qaPairs.filter((_, i) => i !== index))
      }
    }

    const updateQAPair = (index: number, field: 'question' | 'answer', value: string) => {
      const updated = [...qaPairs]
      updated[index][field] = value
      setQaPairs(updated)
    }

    return (
      <div className="space-y-4">
        <Input
          type="text"
          value={textTitle}
          onChange={(e) => setTextTitle(e.target.value)}
          placeholder="Collection title (optional)"
          disabled={uploadProgress.isUploading}
          className="text-sm"
        />

        <div className="space-y-3">
          {qaPairs.map((pair, index) => (
            <div key={index} className="relative rounded-xl border border-border bg-muted/50 p-4 space-y-3">
              {/* Pair number badge + remove */}
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-medium text-muted-foreground">
                  {index + 1}
                </span>
                {qaPairs.length > 1 && (
                  <button
                    onClick={() => removeQAPair(index)}
                    className="text-muted-foreground/60 hover:text-red-500 transition-colors p-0.5"
                    disabled={uploadProgress.isUploading}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Question</label>
                  <Input
                    type="text"
                    value={pair.question}
                    onChange={(e) => updateQAPair(index, 'question', e.target.value)}
                    placeholder="What do users ask?"
                    disabled={uploadProgress.isUploading}
                    maxLength={500}
                    className="text-sm bg-card"
                  />
                  {pair.question.length >= 450 && (
                    <p className={cn("text-xs mt-1", pair.question.length >= 500 ? "text-red-500" : "text-muted-foreground")}>
                      {pair.question.length}/500
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Answer</label>
                  <Textarea
                    value={pair.answer}
                    onChange={(e) => updateQAPair(index, 'answer', e.target.value)}
                    placeholder="The ideal response..."
                    rows={2}
                    disabled={uploadProgress.isUploading}
                    maxLength={1000}
                    className="text-sm bg-card resize-none"
                  />
                  {pair.answer.length >= 900 && (
                    <p className={cn("text-xs mt-1", pair.answer.length >= 1000 ? "text-red-500" : "text-muted-foreground")}>
                      {pair.answer.length}/1000
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addQAPair}
            disabled={uploadProgress.isUploading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-border transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add pair
          </button>
        </div>
      </div>
    )
  }

  /**
   * Enhanced progress display with detailed status for selected URLs and Q&A pairs
   */
  const renderProgress = () => {
    if (!uploadProgress.isUploading && !uploadProgress.error && uploadProgress.progress === 0) {
      return null
    }

    const isSelectedUrlsProcessing = isMonitoring && uploadProgress.totalFiles > 1
    const hasUploadingItems = uploadProgress.uploadingItems && uploadProgress.uploadingItems.length > 0

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground font-medium">
            {uploadProgress.isUploading
              ? isSelectedUrlsProcessing
                ? 'Processing Selected URLs...'
                : 'Uploading...'
              : 'Complete'}
          </span>
          <span className="text-muted-foreground">
            {isSelectedUrlsProcessing
              ? `${uploadProgress.currentFile}/${uploadProgress.totalFiles} URLs`
              : `${uploadProgress.currentFile}/${uploadProgress.totalFiles}`}
          </span>
        </div>

        <div className="w-full bg-muted rounded-full h-3">
          <div
            className={cn(
              "h-3 rounded-full transition-all duration-500",
              uploadProgress.error ? "bg-red-500" : "bg-primary/100"
            )}
            style={{ width: `${uploadProgress.progress}%` }}
          />
        </div>

        {/* Individual Q&A pair or file progress */}
        {hasUploadingItems && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {uploadProgress.uploadingItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                <div className="flex-shrink-0">
                  {item.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {item.status === 'processing' && <RefreshCw className="w-4 h-4 text-primary animate-spin" />}
                  {item.status === 'failed' && <AlertCircle className="w-4 h-4 text-red-500" />}
                  {item.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-border" />}
                </div>
                <span className="flex-1 truncate text-foreground">{item.name}</span>
                <span className={cn(
                  "text-xs font-medium",
                  item.status === 'completed' && "text-green-600",
                  item.status === 'processing' && "text-primary",
                  item.status === 'failed' && "text-red-600",
                  item.status === 'pending' && "text-muted-foreground"
                )}>
                  {item.status === 'completed' && '✓'}
                  {item.status === 'processing' && '...'}
                  {item.status === 'failed' && '✗'}
                  {item.status === 'pending' && '◦'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* We've removed the blue processing indicator as requested */}
        
        {uploadProgress.error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-red-700">{uploadProgress.error}</span>
          </div>
        )}

        {/* Success message for completed selected URLs */}
        {!uploadProgress.isUploading && !uploadProgress.error && uploadProgress.progress === 100 && uploadProgress.totalFiles > 1 && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-green-700">
              Successfully processed {uploadProgress.totalFiles} selected URLs!
            </span>
          </div>
        )}
      </div>
    )
  }

  // ====================================
  // RENDER
  // ====================================

  return (
    <div className="space-y-4">
      {/* Compact Header */}
      {renderCompactHeader()}

      {/* Expanded Upload Interface */}
      {isExpanded && selectedChatbot && (
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Mode Selector */}
            {renderModeSelector()}

            {/* Settings — above mode content */}
            <div className="space-y-3">
              <div className={cn("grid gap-2.5", uploadMode === 'files' ? "grid-cols-2" : "grid-cols-1")}>
                {/* Folder mode — files only */}
                {uploadMode === 'files' && (
                  <label
                    className={cn(
                      "flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-all select-none cursor-pointer",
                      folderMode
                        ? "bg-primary/10 border-primary/20"
                        : "bg-card border-border hover:border-border",
                      uploadProgress.isUploading && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                      folderMode ? "bg-primary/15" : "bg-muted"
                    )}>
                      <FolderOpen className={cn("w-4 h-4", folderMode ? "text-primary" : "text-muted-foreground")} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-tight text-foreground">Folder upload</p>
                      <p className="text-xs text-muted-foreground leading-tight mt-0.5">Upload entire folders at once</p>
                    </div>
                    <Switch
                      checked={folderMode}
                      onCheckedChange={(checked) => !uploadProgress.isUploading && setFolderMode(checked)}
                      disabled={uploadProgress.isUploading}
                    />
                  </label>
                )}

                {/* AI metadata */}
                <label
                  className={cn(
                    "flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-all select-none cursor-pointer",
                    suggestMetadata
                      ? "bg-primary/10 border-primary/20"
                      : "bg-card border-border hover:border-border",
                    uploadProgress.isUploading && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                    suggestMetadata ? "bg-primary/15" : "bg-muted"
                  )}>
                    <Sparkles className={cn("w-4 h-4", suggestMetadata ? "text-primary" : "text-muted-foreground")} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight text-foreground">AI metadata</p>
                    <p className="text-xs text-muted-foreground leading-tight mt-0.5">Auto-detect tags and topics</p>
                  </div>
                  <Switch
                    checked={suggestMetadata}
                    onCheckedChange={(checked) => !uploadProgress.isUploading && setSuggestMetadata(checked)}
                    disabled={uploadProgress.isUploading}
                  />
                </label>
              </div>

              {/* Chunking strategy — files only */}
              {uploadMode === 'files' && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground mr-0.5">Chunks:</span>
                  {([
                    { value: 'auto', label: 'Auto', icon: Sparkles },
                    { value: 'individual', label: 'Individual', icon: LayoutList },
                    { value: 'semantic_groups', label: 'Semantic', icon: Layers },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setChunkingStrategy(opt.value)}
                      disabled={uploadProgress.isUploading}
                      title={
                        opt.value === 'auto' ? 'AI picks the best chunking strategy' :
                        opt.value === 'individual' ? 'One chunk per item or row' :
                        'Group related content together'
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-all border",
                        chunkingStrategy === opt.value
                          ? "bg-primary/10 text-primary border-primary/20 shadow-sm"
                          : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-border"
                      )}
                    >
                      <opt.icon className="w-3 h-3" />
                      {opt.label}
                    </button>
                  ))}
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/60 hover:text-foreground transition-colors cursor-help">
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                        <p className="font-medium mb-1">How your data is split into chunks for search</p>
                        <ul className="space-y-0.5 text-muted-foreground">
                          <li><span className="font-medium text-foreground">Auto:</span> AI decides the best strategy</li>
                          <li><span className="font-medium text-foreground">Individual:</span> One chunk per item/row — best for FAQs & structured data</li>
                          <li><span className="font-medium text-foreground">Semantic:</span> Groups related content — best for long documents</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>

            {/* Required variables for multi-user isolation */}
            {requiredVars.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <KeyRound className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Training Variables</p>
                      <p className="text-xs text-muted-foreground">Tag this data for user-scoped search</p>
                    </div>
                  </div>
                </div>
                <div className={cn("grid gap-3", requiredVars.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
                  {requiredVars.map(v => (
                    <div key={v.name} className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{v.name.replace(/_/g, ' ')}</label>
                      <Input
                        type="text"
                        value={variableValues[v.name] || ''}
                        onChange={(e) => setVariableValues(prev => ({ ...prev, [v.name]: e.target.value }))}
                        placeholder={v.default || `Enter ${v.name.replace(/_/g, ' ')}`}
                        disabled={uploadProgress.isUploading}
                        className="text-sm h-10"
                      />
                    </div>
                  ))}
                </div>
                {!allRequiredVarsFilled && (
                  <p className="text-xs text-destructive flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-destructive" />
                    Fill in all variables before uploading
                  </p>
                )}
              </div>
            )}

            {/* Upload Interface based on mode */}
            {uploadMode === 'files' && renderFileUpload()}
            {uploadMode === 'url' && renderUrlUpload()}
            {uploadMode === 'text' && renderTextUpload()}

            {/* Progress */}
            {renderProgress()}
            
            
            {/* Processing Status - Show if there's background activity or recent activity */}
            {(uploadStatus && (uploadStatus.pending > 0 || uploadStatus.processing > 0 || uploadStatus.is_active)) && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-foreground">Processing Status</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkUploadStatus}
                    disabled={isMonitoring}
                  >
                    <RefreshCw className={cn("w-3 h-3", isMonitoring && "animate-spin")} />
                  </Button>
                </div>
                
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="p-2 bg-yellow-50 rounded">
                    <div className="text-sm font-bold text-yellow-600">{uploadStatus.pending}</div>
                    <div className="text-xs text-yellow-700">Pending</div>
                  </div>
                  <div className="p-2 bg-primary/10 rounded">
                    <div className="text-sm font-bold text-primary">{uploadStatus.processing}</div>
                    <div className="text-xs text-primary">Processing</div>
                  </div>
                  <div className="p-2 bg-green-50 rounded">
                    <div className="text-sm font-bold text-green-600">{uploadStatus.completed}</div>
                    <div className="text-xs text-green-700">Completed</div>
                  </div>
                  <div className="p-2 bg-red-50 rounded">
                    <div className="text-sm font-bold text-red-600">{uploadStatus.failed}</div>
                    <div className="text-xs text-red-700">Failed</div>
                  </div>
                </div>

                {/* Per-file warnings removed — alert icon shown inline in file details instead */}
                
                {/* Expandable file details */}
                {uploadStatus.files && uploadStatus.files.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setShowFileDetails(!showFileDetails)}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showFileDetails ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                      <span>{showFileDetails ? 'Hide' : 'Show'} file details ({uploadStatus.files.length})</span>
                    </button>

                    {showFileDetails && (
                      <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                        {uploadStatus.files.map((file: any, idx: number) => {
                          const isZeroChunks = (file.status === 'completed' || file.status === 'success') && file.chunks_stored === 0
                          return (
                          <div
                            key={`${file.filename}-${idx}`}
                            className="flex items-center gap-2 px-2.5 py-1.5 bg-card rounded border border-border text-xs"
                          >
                            {file.status === 'processing' ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary flex-shrink-0" />
                            ) : file.status === 'pending' ? (
                              <div className="w-3.5 h-3.5 rounded-full border-2 border-yellow-400 flex-shrink-0" />
                            ) : isZeroChunks ? (
                              <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            ) : file.status === 'completed' || file.status === 'success' ? (
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            )}
                            <span className="truncate flex-1 text-foreground" title={file.filename}>
                              {file.filename}
                            </span>
                            {isZeroChunks && (
                              <span className="text-amber-500 flex-shrink-0" title="File processed but no data extracted">0 chunks</span>
                            )}
                            <span className={cn(
                              "flex-shrink-0 font-medium",
                              file.status === 'processing' && 'text-primary',
                              file.status === 'pending' && 'text-yellow-600',
                              isZeroChunks && 'text-amber-600',
                              !isZeroChunks && (file.status === 'completed' || file.status === 'success') && 'text-green-600',
                              file.status === 'failed' && 'text-red-600'
                            )}>
                              {file.status === 'failed' ? 'failed' : file.status === 'success' ? 'completed' : file.status}
                            </span>
                          </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {uploadStatus.is_active && (
                  <div className="mt-3 flex items-center gap-2 p-2 bg-primary/10 rounded text-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-primary">Background processing active...</span>
                  </div>
                )}
                
                {!uploadStatus.is_active && uploadStatus.completed > 0 && (
                  <div className={cn(
                    "mt-3 flex items-center gap-2 p-2 rounded text-sm",
                    uploadStatus.failed > 0 ? "bg-amber-50" : "bg-green-50"
                  )}>
                    <CheckCircle className={cn("w-4 h-4", uploadStatus.failed > 0 ? "text-amber-500" : "text-green-500")} />
                    <span className={uploadStatus.failed > 0 ? "text-amber-700" : "text-green-700"}>
                      Processing completed! {uploadStatus.completed} {uploadStatus.completed === 1 ? 'item' : 'items'} processed successfully.
                      {uploadStatus.failed > 0 && ` ${uploadStatus.failed} failed.`}
                    </span>
                  </div>
                )}

                {!uploadStatus.is_active && uploadStatus.completed === 0 && uploadStatus.failed > 0 && (
                  <div className="mt-3 flex items-center gap-2 p-2 bg-red-50 rounded text-sm">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-700">
                      Processing failed for all {uploadStatus.failed} {uploadStatus.failed === 1 ? 'file' : 'files'}. Check file details above.
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Upload button */}
            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleUpload}
                disabled={!canUpload()}
                className="flex-1 bg-primary hover:bg-primary/90 h-10 rounded-lg text-sm font-medium"
              >
                {uploadProgress.isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {uploadModes[uploadMode].title}
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                disabled={uploadProgress.isUploading}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link Discovery Modal */}
      {selectedChatbot && (
        <LinkDiscoveryModal
          isOpen={isLinkDiscoveryOpen}
          onClose={() => setIsLinkDiscoveryOpen(false)}
          initialUrl={urlInput.trim()}
          selectedChatbot={selectedChatbot}
          onLinksSelected={(urls) => {
            setIsLinkDiscoveryOpen(false)
            processUrl(urls)
          }}
          onDiscoveryComplete={() => {}}
          suggestMetadata={suggestMetadata}
        />
      )}
    </div>
  )
}

export default ModernUploadInterface
